import React, { useCallback } from 'react';
import Button from '../../../../components/common/Button/Button';
import EmptyState from '../../../../components/common/EmptyState/EmptyState';
import Loading from '../../../../components/common/Loading/Loading';
import SearchFilterBar from '../../../../components/common/SearchFilterBar/SearchFilterBar';
import type { ExerciseItem } from '../../../../types/exercise';
import TrainingCatalogCard from '../../../Training/components/TrainingCatalogCard/TrainingCatalogCard';
import { useEscapeToClose } from '../../../../hooks/useEscapeToClose';
import styles from './RelatedExercisePickerModal.module.css';

interface RelatedExercisePickerModalProps {
  open: boolean;
  exercises: ExerciseItem[];
  loading: boolean;
  searchTerm: string;
  fixedGroupLabels: string[];
  selectedIds: number[];
  onClose: () => void;
  onConfirm: () => void;
  onSearchChange: (value: string) => void;
  onToggleSelect: (exercise: ExerciseItem) => void;
}

const RelatedExercisePickerModal: React.FC<RelatedExercisePickerModalProps> = ({
  open,
  exercises,
  loading,
  searchTerm,
  fixedGroupLabels,
  selectedIds,
  onClose,
  onConfirm,
  onSearchChange,
  onToggleSelect,
}) => {
  useEscapeToClose(onClose, open);

  const handleDragStart = useCallback((event: React.DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
  }, []);

  if (!open) {
    return null;
  }

  return (
    <div className={styles.overlay}>
      <div className={styles.modal} onClick={(event) => event.stopPropagation()}>
        <div className={styles.header}>
          <h3 className={styles.title}>Adicionar exercícios relacionados</h3>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Fechar modal"
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        <div className={styles.filtersRow}>
          <SearchFilterBar
            searchValue={searchTerm}
            onSearchChange={onSearchChange}
            searchPlaceholder="Pesquisar"
            loading={loading}
            className={styles.searchBar}
            searchWrapperClassName={styles.searchWrapper}
            inputClassName={styles.searchInput}
          />

          <div className={styles.fixedGroupsWrapper}>
            <span className={styles.fixedGroupsLabel}>Categoria / Grupo Muscular</span>
            <div className={styles.fixedGroupsList}>
              {fixedGroupLabels.length > 0 ? fixedGroupLabels.map((groupLabel) => (
                <span key={groupLabel} className={styles.fixedGroupChip}>{groupLabel}</span>
              )) : (
                <span className={styles.fixedGroupChipPlaceholder}>Selecione um grupo muscular no exercício principal</span>
              )}
            </div>
          </div>
        </div>

        <div className={styles.content}>
          {loading ? (
            <div className={styles.loadingContainer}>
              <Loading inline />
            </div>
          ) : exercises.length === 0 ? (
            <div className={styles.emptyContainer}>
              <EmptyState message="Nenhum exercício encontrado com os filtros selecionados." showImage={false} icon="search_off" iconSize={28} />
            </div>
          ) : (
            <div className={styles.catalogList}>
              {exercises.map((exercise) => (
                <TrainingCatalogCard
                  key={exercise.id}
                  exercise={exercise}
                  isSelected={selectedIds.includes(exercise.id)}
                  showSelectedCheck
                  onSelect={onToggleSelect}
                  onDragStart={handleDragStart}
                />
              ))}
            </div>
          )}
        </div>

        <div className={styles.footer}>
          <span className={styles.selectionCount}>{selectedIds.length} selecionado(s)</span>

          <div className={styles.footerActions}>
            <Button variant="ghost" size="small" type="button" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              size="small"
              type="button"
              onClick={onConfirm}
              disabled={selectedIds.length === 0}
            >
              Adicionar selecionados
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RelatedExercisePickerModal;
